// Rotas falsas de Identity (GIY-037; Parte 3.6): "Minha conta" (sessões com step-up, troca de senha), administração de
// usuários de rede e de tenant, contas temporárias, redefinição de MFA em duas chaves e clientes OIDC. Regras de escopo
// iguais às do servidor: `/network/*` sem acesso de rede → 403 PLT_NETWORK_SCOPE_REQUIRED; tenant fora de `contexts[]` → 404.
import type { MeResponse, components } from '@giyus/api-client';

import type { FakeSessionAuth } from '../auth/FakeAuthProvider';
import { ACTIVE_GIYUS_DIRECTORY_USERS } from '../auth/greenfield';
import { FakeApiError, jsonResponse, noContent, problemResponse, readJsonBody } from './http';
import {
  type FakeIdentityState,
  type FakeOidcClient,
  type FakeUser,
  type IdentitySessionResponse,
  fakeId,
  shiftIso,
  toSummary,
} from './identityStore';

type ChangeReason = components['schemas']['IdentityChangeReasonRequest'];

export interface IdentityRouteContext {
  readonly state: FakeIdentityState;
  readonly me: MeResponse;
  readonly correlationId: string | null;
  readonly auth: FakeSessionAuth;
}

const COMMON_PASSWORDS = new Set(['Password123!', 'password1234', 'Senha12345678']);
const WRONG_CURRENT_PASSWORD = 'wrong-password';
const NEW_SECRET = 'fake-secret-8f3a1c2d9e4b7a6f5c3d2e1b0a9f8e7d';

function record(body: unknown): Record<string, unknown> {
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
}

function requireChangeReason(body: unknown): ChangeReason {
  const reason = record(body)['changeReason'];
  const code =
    typeof reason === 'object' && reason !== null ? (reason as { code?: unknown }).code : null;
  if (typeof code !== 'string' || code.trim() === '') {
    throw new FakeApiError(422, 'PLT_CHANGE_REASON_REQUIRED');
  }
  const text = (reason as { text?: unknown }).text;
  return { code, text: typeof text === 'string' ? text : null };
}

function requireText(body: unknown, field: string): string {
  const value = record(body)[field];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new FakeApiError(422, 'PLT_VALIDATION', {
      errors: [{ field, code: 'REQUIRED', message: 'Required.' }],
    });
  }
  return value.trim();
}

function requireStepUp(auth: FakeSessionAuth): void {
  if (!auth.isRecentlyAuthenticated()) {
    throw new FakeApiError(401, 'PLT_STEP_UP_REQUIRED', { error: 'step_up_required' });
  }
}

function findUser(state: FakeIdentityState, id: string | undefined): FakeUser {
  const found = state.users.find((entry) => entry.id === id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function issueInvitation(state: FakeIdentityState, target: FakeUser, tenantId: string | null) {
  const invitation = {
    id: fakeId(state),
    issuedAt: state.base,
    expiresAt: shiftIso(state.base, 72 * 60),
    tenantId,
  };
  target.pendingInvitation = invitation;
  if (tenantId && !target.invitationTenantIds.includes(tenantId)) {
    target.invitationTenantIds.push(tenantId);
  }
  return invitation;
}

function listUsers(state: FakeIdentityState, url: URL, tenantId: string | null) {
  const status = url.searchParams.get('status');
  const kind = url.searchParams.get('kind');
  const personId = url.searchParams.get('personId');
  const tenantFilter = tenantId ?? url.searchParams.get('tenantId');
  return state.users
    .filter((entry) => (tenantFilter ? entry.invitationTenantIds.includes(tenantFilter) : true))
    .filter((entry) => (status ? entry.status === status : true))
    .filter((entry) => (kind ? entry.kind === kind : true))
    .filter((entry) => (personId ? entry.personId === personId : true))
    .sort((a, b) => a.email.localeCompare(b.email))
    .map(toSummary);
}

function createUser(state: FakeIdentityState, body: unknown, tenantId: string | null) {
  const personId = requireText(body, 'personId');
  const email = requireText(body, 'email').toLowerCase();
  const displayName = requireText(body, 'displayName');
  const locale = record(body)['preferredLocale'];
  const existing = state.users.find((entry) => entry.personId === personId);
  if (existing) {
    // REQ-AUTH-020: idempotente por pessoa — projeção mínima, convite só se ainda Invited.
    const reissued =
      existing.status === 'Invited' ? issueInvitation(state, existing, tenantId) : null;
    return { status: 200, body: { user: toSummary(existing), invitation: reissued } };
  }
  if (state.users.some((entry) => entry.email === email)) {
    throw new FakeApiError(409, 'AUTH_EMAIL_IN_USE');
  }
  const isActiveEntraUser = ACTIVE_GIYUS_DIRECTORY_USERS.some(
    (u) => u.email.toLowerCase() === email,
  );
  const created: FakeUser = {
    id: fakeId(state),
    kind: 'Human',
    status: isActiveEntraUser ? 'Active' : 'Invited',
    email,
    displayName,
    preferredLocale: typeof locale === 'string' && locale ? locale : 'pt-BR',
    expiresAt: null,
    lockedUntil: null,
    failedAttemptCycle: 0,
    personId,
    invitationTenantIds: [],
    pendingInvitation: null,
    mfa: isActiveEntraUser
      ? { enrolled: true, methods: ['Totp'] }
      : { enrolled: false, methods: [] },
    sessions: [],
    grant: null,
  };
  state.users.push(created);
  const invitation = issueInvitation(state, created, tenantId);
  return { status: 201, body: { user: toSummary(created), invitation } };
}

function createTemporaryAccount(
  state: FakeIdentityState,
  body: unknown,
  actorId: string,
  tenantId: string | null,
) {
  const personId = requireText(body, 'personId');
  const email = requireText(body, 'email').toLowerCase();
  const displayName = requireText(body, 'displayName');
  const reason = requireText(body, 'reason');
  const scopeSummary = requireText(body, 'scopeSummary');
  const expiresAt = requireText(body, 'expiresAt');
  const expiry = new Date(expiresAt).getTime();
  const now = new Date(state.base).getTime();
  if (Number.isNaN(expiry) || expiry <= now || expiry > now + 90 * 24 * 60 * 60_000) {
    throw new FakeApiError(422, 'AUTH_TEMPORARY_EXPIRY_OUT_OF_RANGE');
  }
  if (state.users.some((entry) => entry.email === email || entry.personId === personId)) {
    throw new FakeApiError(409, 'AUTH_EMAIL_IN_USE');
  }
  const locale = record(body)['preferredLocale'];
  const created: FakeUser = {
    id: fakeId(state),
    kind: 'TemporaryHuman',
    status: 'Invited',
    email,
    displayName,
    preferredLocale: typeof locale === 'string' && locale ? locale : 'pt-BR',
    expiresAt: new Date(expiry).toISOString(),
    lockedUntil: null,
    failedAttemptCycle: 0,
    personId,
    invitationTenantIds: [],
    pendingInvitation: null,
    mfa: { enrolled: false, methods: [] },
    sessions: [],
    grant: {
      grantId: fakeId(state),
      requestedBy: actorId,
      reason,
      scopeSummary,
      extendedBy: null,
      extendedAt: null,
      extensionReason: null,
    },
  };
  state.users.push(created);
  const invitation = issueInvitation(state, created, tenantId);
  return temporaryResponse(created, invitation);
}

function temporaryResponse(
  entry: FakeUser,
  invitation: components['schemas']['IdentityInvitationSummaryResponse'] | null,
) {
  const grant = entry.grant;
  if (!grant) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return {
    user: toSummary(entry),
    grantId: grant.grantId,
    requestedBy: grant.requestedBy,
    reason: grant.reason,
    scopeSummary: grant.scopeSummary,
    expiresAt: entry.expiresAt ?? '',
    extendedBy: grant.extendedBy,
    extendedAt: grant.extendedAt,
    extensionReason: grant.extensionReason,
    invitation,
  };
}

function extendTemporaryAccount(
  state: FakeIdentityState,
  id: string | undefined,
  body: unknown,
  actorId: string,
  tenantId: string | null,
) {
  const entry = state.users.find(
    (candidate) =>
      candidate.id === id &&
      candidate.kind === 'TemporaryHuman' &&
      (tenantId === null || candidate.invitationTenantIds.includes(tenantId)),
  );
  if (!entry?.grant) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  const reasonText = record(body)['reason'];
  if (typeof reasonText !== 'string' || reasonText.trim() === '') {
    throw new FakeApiError(422, 'PLT_CHANGE_REASON_REQUIRED');
  }
  const newExpiresAt = requireText(body, 'newExpiresAt');
  const next = new Date(newExpiresAt).getTime();
  const current = new Date(entry.expiresAt ?? state.base).getTime();
  const now = new Date(state.base).getTime();
  if (Number.isNaN(next) || next <= current || next > now + 90 * 24 * 60 * 60_000) {
    throw new FakeApiError(422, 'AUTH_TEMPORARY_EXPIRY_OUT_OF_RANGE');
  }
  entry.expiresAt = new Date(next).toISOString();
  entry.grant.extendedBy = actorId;
  entry.grant.extendedAt = state.base;
  entry.grant.extensionReason = reasonText.trim();
  return temporaryResponse(entry, null);
}

function clientResponse(client: FakeOidcClient) {
  return {
    id: client.id,
    clientId: client.clientId,
    displayName: client.displayName,
    kind: client.kind,
    status: client.status,
    redirectUris: client.redirectUris,
    postLogoutRedirectUris: client.postLogoutRedirectUris,
    allowedScopes: client.allowedScopes,
    allowedGrantTypes: client.allowedGrantTypes,
    requirePkce: client.requirePkce,
    ownerUserId: client.ownerUserId,
    secretExpiresAt: client.secretExpiresAt,
  };
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
    : [];
}

function createClient(state: FakeIdentityState, body: unknown) {
  const clientId = requireText(body, 'clientId');
  const displayName = requireText(body, 'displayName');
  const kindRaw = requireText(body, 'kind');
  const kind =
    kindRaw.toLowerCase() === 'public'
      ? 'Public'
      : kindRaw.toLowerCase() === 'confidential'
        ? 'Confidential'
        : null;
  const allowedScopes = stringList(record(body)['allowedScopes']);
  if (!kind || allowedScopes.length === 0) {
    throw new FakeApiError(422, 'PLT_VALIDATION');
  }
  const redirectUris = stringList(record(body)['redirectUris']);
  if (kind === 'Public' && redirectUris.length === 0) {
    throw new FakeApiError(422, 'PLT_VALIDATION', {
      errors: [
        {
          field: 'redirectUris',
          code: 'REQUIRED',
          message: 'Public clients require at least one redirect URI.',
        },
      ],
    });
  }
  if (state.clients.some((client) => client.clientId === clientId)) {
    throw new FakeApiError(409, 'AUTH_CLIENT_ID_IN_USE');
  }
  const owner = record(body)['ownerUserId'];
  const client: FakeOidcClient = {
    id: fakeId(state),
    clientId,
    displayName,
    kind,
    status: 'Active',
    redirectUris,
    postLogoutRedirectUris: stringList(record(body)['postLogoutRedirectUris']),
    allowedScopes,
    allowedGrantTypes:
      kind === 'Public' ? ['authorization_code', 'refresh_token'] : ['client_credentials'],
    requirePkce: kind === 'Public',
    ownerUserId: typeof owner === 'string' && owner ? owner : null,
    secretExpiresAt: kind === 'Confidential' ? shiftIso(state.base, 180 * 24 * 60) : null,
  };
  state.clients.push(client);
  return {
    client: clientResponse(client),
    clientSecret: kind === 'Confidential' ? NEW_SECRET : null,
  };
}

function updateClient(state: FakeIdentityState, id: string | undefined, body: unknown) {
  const client = state.clients.find((candidate) => candidate.id === id);
  if (!client) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  requireChangeReason(body);
  const displayName = requireText(body, 'displayName');
  const allowedScopes = stringList(record(body)['allowedScopes']);
  if (allowedScopes.length === 0) {
    throw new FakeApiError(422, 'PLT_VALIDATION');
  }
  const enabled = record(body)['enabled'];
  const updated: FakeOidcClient = {
    ...client,
    displayName,
    allowedScopes,
    redirectUris: stringList(record(body)['redirectUris']),
    postLogoutRedirectUris: stringList(record(body)['postLogoutRedirectUris']),
    status: enabled === false ? 'Disabled' : 'Active',
  };
  state.clients.splice(state.clients.indexOf(client), 1, updated);
  return clientResponse(updated);
}

function rotateSecret(state: FakeIdentityState, id: string | undefined, body: unknown) {
  const client = state.clients.find((candidate) => candidate.id === id);
  if (!client) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  requireChangeReason(body);
  if (client.kind !== 'Confidential') {
    throw new FakeApiError(422, 'PLT_VALIDATION', {
      errors: [{ field: 'kind', code: 'INVALID', message: 'Public clients have no secret.' }],
    });
  }
  client.secretExpiresAt = shiftIso(state.base, 180 * 24 * 60);
  return { client: clientResponse(client), clientSecret: NEW_SECRET };
}

function changePassword(body: unknown, auth: FakeSessionAuth) {
  requireStepUp(auth);
  const current = requireText(body, 'currentPassword');
  const next = requireText(body, 'newPassword');
  if (current === WRONG_CURRENT_PASSWORD) {
    throw new FakeApiError(422, 'AUTH_PASSWORD_INCORRECT');
  }
  if (next.length < 12) {
    throw new FakeApiError(422, 'AUTH_PASSWORD_TOO_SHORT', {
      errors: [
        {
          field: 'newPassword',
          code: 'AUTH_PASSWORD_TOO_SHORT',
          message: 'At least 12 characters.',
        },
      ],
    });
  }
  if (COMMON_PASSWORDS.has(next)) {
    throw new FakeApiError(422, 'AUTH_PASSWORD_TOO_COMMON');
  }
  if (next === current) {
    throw new FakeApiError(422, 'AUTH_PASSWORD_REUSED');
  }
}

async function handleMeRoutes(
  request: Request,
  rest: readonly string[],
  ctx: IdentityRouteContext,
): Promise<Response | null> {
  const method = request.method.toUpperCase();
  const caller = findUser(ctx.state, ctx.me.user.id);
  const currentSessionId = ctx.me.session?.id ?? null;
  if (rest[0] === 'sessions' && rest.length === 1 && method === 'GET') {
    const sessions: IdentitySessionResponse[] = caller.sessions.map((entry) => ({
      ...entry,
      isCurrent: entry.id === currentSessionId,
    }));
    return jsonResponse(sessions);
  }
  if (rest[0] === 'sessions' && rest.length === 2 && method === 'DELETE') {
    requireStepUp(ctx.auth);
    const target = caller.sessions.find((entry) => entry.id === rest[1]);
    if (!target) {
      throw new FakeApiError(404, 'PLT_NOT_FOUND');
    }
    caller.sessions = caller.sessions.filter((entry) => entry.id !== target.id);
    return noContent();
  }
  if (rest[0] === 'password' && rest.length === 1 && method === 'POST') {
    changePassword(await readJsonBody(request), ctx.auth);
    return noContent();
  }
  return null;
}

async function handleNetworkRoutes(
  request: Request,
  rest: readonly string[],
  ctx: IdentityRouteContext,
): Promise<Response | null> {
  const method = request.method.toUpperCase();
  const { state } = ctx;
  const actorId = ctx.me.user.id;
  if (rest[0] === 'users') {
    if (rest.length === 1 && method === 'GET') {
      return jsonResponse(listUsers(state, new URL(request.url), null));
    }
    if (rest.length === 1 && method === 'POST') {
      const created = createUser(state, await readJsonBody(request), null);
      return jsonResponse(created.body, created.status);
    }
    const target = findUser(state, rest[1]);
    if (rest.length === 2 && method === 'GET') {
      return jsonResponse({
        user: toSummary(target),
        sessions: target.sessions,
        mfa: target.mfa,
        pendingInvitation: target.pendingInvitation,
      });
    }
    if (rest.length === 3 && method === 'POST') {
      const body = await readJsonBody(request);
      switch (rest[2]) {
        case 'invitations': {
          if (target.status !== 'Invited') {
            throw new FakeApiError(422, 'AUTH_USER_ALREADY_ACTIVE');
          }
          return jsonResponse({
            user: toSummary(target),
            invitation: issueInvitation(state, target, null),
          });
        }
        case 'disable': {
          requireChangeReason(body);
          target.status = 'Disabled';
          target.sessions = [];
          return jsonResponse(toSummary(target));
        }
        case 'lock': {
          requireChangeReason(body);
          const minutes = record(body)['durationMinutes'];
          const duration = typeof minutes === 'number' && minutes > 0 ? minutes : 24 * 60;
          target.lockedUntil = shiftIso(state.base, duration);
          return jsonResponse({
            userId: target.id,
            lockedUntil: target.lockedUntil,
            failedAttemptCycle: target.failedAttemptCycle,
          });
        }
        case 'unlock': {
          requireChangeReason(body);
          target.lockedUntil = null;
          target.failedAttemptCycle = 0;
          return jsonResponse({ userId: target.id, lockedUntil: null, failedAttemptCycle: 0 });
        }
        case 'mfa-reset-requests': {
          requireText(body, 'identityVerificationNote');
          const open = state.mfaResetRequests.some(
            (entry) =>
              entry.userId === target.id &&
              (entry.status === 'PendingApproval' || entry.status === 'Approved'),
          );
          if (open) {
            throw new FakeApiError(409, 'AUTH_MFA_RESET_PENDING');
          }
          const created = {
            id: fakeId(state),
            userId: target.id,
            status: 'PendingApproval',
            requestedBy: actorId,
            requestedAt: state.base,
            approvedBy: null,
            approvedAt: null,
            windowExpiresAt: null,
          };
          state.mfaResetRequests.push(created);
          return jsonResponse(created, 201);
        }
        default:
          return null;
      }
    }
    if (
      rest.length === 4 &&
      rest[2] === 'sessions' &&
      rest[3] === 'revoke-all' &&
      method === 'POST'
    ) {
      requireChangeReason(await readJsonBody(request));
      const revokedCount = target.sessions.length;
      target.sessions = [];
      return jsonResponse({ revokedCount });
    }
    return null;
  }
  if (
    rest[0] === 'mfa-reset-requests' &&
    rest.length === 3 &&
    rest[2] === 'approve' &&
    method === 'POST'
  ) {
    const found = state.mfaResetRequests.find((entry) => entry.id === rest[1]);
    if (!found) {
      throw new FakeApiError(404, 'PLT_NOT_FOUND');
    }
    if (found.requestedBy === actorId) {
      throw new FakeApiError(422, 'AUTHZ_SOD_VIOLATION');
    }
    if (found.status !== 'PendingApproval') {
      throw new FakeApiError(409, 'AUTH_MFA_RESET_PENDING');
    }
    const approved = {
      ...found,
      status: 'Approved',
      approvedBy: actorId,
      approvedAt: state.base,
      windowExpiresAt: shiftIso(state.base, 24 * 60),
    };
    state.mfaResetRequests.splice(state.mfaResetRequests.indexOf(found), 1, approved);
    return jsonResponse(approved);
  }
  if (rest[0] === 'temporary-accounts') {
    if (rest.length === 1 && method === 'POST') {
      return jsonResponse(
        createTemporaryAccount(state, await readJsonBody(request), actorId, null),
        201,
      );
    }
    if (rest.length === 3 && rest[2] === 'extend' && method === 'POST') {
      return jsonResponse(
        extendTemporaryAccount(state, rest[1], await readJsonBody(request), actorId, null),
      );
    }
    return null;
  }
  if (rest[0] === 'oidc-clients') {
    if (rest.length === 1 && method === 'GET') {
      return jsonResponse(state.clients.map(clientResponse));
    }
    if (rest.length === 1 && method === 'POST') {
      return jsonResponse(createClient(state, await readJsonBody(request)), 201);
    }
    if (rest.length === 2 && method === 'PUT') {
      return jsonResponse(updateClient(state, rest[1], await readJsonBody(request)));
    }
    if (rest.length === 3 && rest[2] === 'rotate-secret' && method === 'POST') {
      return jsonResponse(rotateSecret(state, rest[1], await readJsonBody(request)));
    }
    return null;
  }
  return null;
}

async function handleTenantRoutes(
  request: Request,
  tenantId: string,
  rest: readonly string[],
  ctx: IdentityRouteContext,
): Promise<Response | null> {
  const method = request.method.toUpperCase();
  const { state } = ctx;
  const actorId = ctx.me.user.id;
  const linked = (id: string | undefined): FakeUser => {
    const found = state.users.find(
      (entry) => entry.id === id && entry.invitationTenantIds.includes(tenantId),
    );
    if (!found) {
      throw new FakeApiError(404, 'PLT_NOT_FOUND');
    }
    return found;
  };
  if (rest[0] === 'users') {
    if (rest.length === 1 && method === 'GET') {
      return jsonResponse(listUsers(state, new URL(request.url), tenantId));
    }
    if (rest.length === 1 && method === 'POST') {
      const created = createUser(state, await readJsonBody(request), tenantId);
      return jsonResponse(created.body, created.status);
    }
    if (rest.length === 2 && method === 'GET') {
      const target = linked(rest[1]);
      const pending =
        target.pendingInvitation?.tenantId === tenantId ? target.pendingInvitation : null;
      return jsonResponse({ user: toSummary(target), invitation: pending });
    }
    if (rest.length === 3 && rest[2] === 'invitations' && method === 'POST') {
      const target = linked(rest[1]);
      if (target.status !== 'Invited') {
        throw new FakeApiError(422, 'AUTH_USER_ALREADY_ACTIVE');
      }
      return jsonResponse({
        user: toSummary(target),
        invitation: issueInvitation(state, target, tenantId),
      });
    }
    return null;
  }
  if (rest[0] === 'temporary-accounts') {
    if (rest.length === 1 && method === 'POST') {
      return jsonResponse(
        createTemporaryAccount(state, await readJsonBody(request), actorId, tenantId),
        201,
      );
    }
    if (rest.length === 3 && rest[2] === 'extend' && method === 'POST') {
      return jsonResponse(
        extendTemporaryAccount(state, rest[1], await readJsonBody(request), actorId, tenantId),
      );
    }
    return null;
  }
  return null;
}

export async function handleIdentityRoute(
  request: Request,
  segments: readonly string[],
  ctx: IdentityRouteContext,
): Promise<Response | null> {
  try {
    if (segments[0] === 'me' && (segments[1] === 'sessions' || segments[1] === 'password')) {
      return await handleMeRoutes(request, segments.slice(1), ctx);
    }
    if (segments[0] === 'network' && segments[1] === 'identity') {
      if (!ctx.me.networkAccess.hasAccess) {
        // REQ-AUTHZ-026: única situação em que a API responde 403 (nunca revela o que existe).
        throw new FakeApiError(403, 'PLT_NETWORK_SCOPE_REQUIRED');
      }
      return await handleNetworkRoutes(request, segments.slice(2), ctx);
    }
    if (segments[0] === 'tenants' && segments[1] && segments[2] === 'identity') {
      const tenantId = segments[1];
      if (!ctx.me.contexts.some((context) => context.tenantId === tenantId)) {
        throw new FakeApiError(404, 'PLT_NOT_FOUND');
      }
      return await handleTenantRoutes(request, tenantId, segments.slice(3), ctx);
    }
    return null;
  } catch (error: unknown) {
    if (error instanceof FakeApiError) {
      return problemResponse(error.status, error.errorCode, ctx.correlationId, error.extra);
    }
    throw error;
  }
}
